import { Dialog, DialogContent, DialogTitle, DialogDescription } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import type { Attachment } from '@/gen/cortado/v1/attachments_pb'
import { formatTimestampWithTime } from '@/lib/date-utils'
import { formatBytes } from '@/lib/file-utils'
import { formatFullName } from '@/lib/utils'
import { Download, File as FileIcon } from 'lucide-react'

interface AttachmentPreviewProps {
  attachment: Attachment | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function AttachmentPreview({ attachment, open, onOpenChange }: AttachmentPreviewProps) {
  if (!attachment) return null

  const url = attachment.downloadUrl
  const isImage = attachment.contentType.startsWith('image/')
  const isPdf = attachment.contentType === 'application/pdf'

  const createdByName = formatFullName(
    attachment.createdByUser?.firstName,
    attachment.createdByUser?.lastName,
    'Unknown'
  )
  const createdAt = formatTimestampWithTime(attachment.createdAt)

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90dvh] gap-0 overflow-hidden p-0 sm:max-w-5xl">
        <div className="flex min-w-0 items-start justify-between gap-4 border-b px-6 py-4 pr-12">
          <div className="min-w-0">
            <DialogTitle
              className="truncate text-sm font-medium text-foreground"
              title={attachment.filename}
            >
              {attachment.filename}
            </DialogTitle>
            <DialogDescription className="mt-1 text-xs text-muted-foreground">
              {formatBytes(attachment.sizeBytes)} &middot; Uploaded by {createdByName} &middot;{' '}
              {createdAt}
            </DialogDescription>
          </div>
          {url && (
            <Button type="button" variant="outline" size="sm" asChild>
              <a href={url} target="_blank" rel="noreferrer">
                <Download className="h-4 w-4" />
                Download
              </a>
            </Button>
          )}
        </div>
        <div className="max-h-[78dvh] overflow-auto bg-muted/30">
          {isImage && url && (
            <div className="flex items-center justify-center p-4">
              <img
                src={url}
                alt={attachment.filename}
                className="max-h-[74dvh] max-w-full object-contain"
              />
            </div>
          )}
          {isPdf && url && (
            <iframe
              src={url}
              title={attachment.filename}
              className="h-[74dvh] w-full border-0 bg-background"
            />
          )}
          {!isImage && !isPdf && url && (
            <div className="flex min-h-[420px] flex-col items-center justify-center gap-3 p-8 text-center">
              <div className="flex h-16 w-16 items-center justify-center rounded-lg border bg-background">
                <FileIcon className="h-7 w-7 text-muted-foreground" />
              </div>
              <div className="space-y-1">
                <p className="text-sm font-medium">No inline preview available</p>
                <p className="text-xs text-muted-foreground">
                  Download this file to view it in the correct app.
                </p>
              </div>
            </div>
          )}
          {!url && (
            <div className="flex min-h-[320px] items-center justify-center p-8 text-center text-sm text-muted-foreground">
              Preview link unavailable
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  )
}
